import type { Locale } from "@colorglo/i18n";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api.ts";
import { keys } from "../lib/query.ts";

/**
 * Every server call the calendar makes.
 *
 * Thin on purpose: each hook unwraps Eden's `{ data, error }` into a promise that resolves or
 * throws, and does nothing else. The `error` branch throws `error.value` rather than the
 * wrapper, so what reaches a component's `error` is the `{ error: { code, message } }`
 * envelope from docs/API.md and `isApiError` recognises it — which is what lets one toast
 * render every failure in this app without a switch per call site.
 */

/** Eden hands back `{ data, error }`; React Query wants a throw. */
async function unwrap<T>(promise: Promise<{ data: T | null; error: unknown }>): Promise<T> {
  const { data, error } = await promise;
  if (error !== null) {
    // Eden wraps the body in `.value`. Unwrapping here means no caller has to know that.
    throw (error as { value?: unknown }).value ?? error;
  }
  if (data === null) throw new Error("the API returned no body");
  return data;
}

export function useMe() {
  return useQuery({
    queryKey: keys.me(),
    queryFn: () => unwrap(api.v1.me.get()),
    // Who you are and where you work does not change while the tab is open. Refetching it
    // on every calendar navigation is a round trip for an answer that cannot have moved.
    staleTime: 5 * 60_000,
  });
}

/**
 * The signed-in user's own language.
 *
 * Invalidating `me` is what re-renders the app in the new language: `useLocale` reads this
 * query, so every `useT` in the tree follows from one refetch and nothing has to be told.
 */
export function useSetLocale() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (locale: Locale) => unwrap(api.v1.me.locale.patch({ locale })),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me() }),
  });
}

/** The grid's chrome: columns, working hours, holidays, snap granularity. */
export function useCalendarChrome(franchiseId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: keys.calendar(franchiseId ?? "", from, to),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(
        api.v1
          .franchises({ franchiseId: franchiseId as string })
          .calendar.get({ query: { from, to } }),
      ),
    // Resources and opening hours change on a settings form, not during a shift.
    staleTime: 5 * 60_000,
  });
}

export function useAppointments(franchiseId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: keys.appointments(franchiseId ?? "", from, to),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(
        api.v1
          .franchises({ franchiseId: franchiseId as string })
          .appointments.get({ query: { from, to } }),
      ),
  });
}

export function useBlockers(franchiseId: string | null, from: string, to: string) {
  return useQuery({
    queryKey: keys.blockers(franchiseId ?? "", from, to),
    enabled: franchiseId !== null,
    queryFn: () =>
      unwrap(
        api.v1
          .franchises({ franchiseId: franchiseId as string })
          .blockers.get({ query: { from, to } }),
      ),
  });
}

/**
 * The wire shapes, inferred from the API's own route graph rather than re-declared.
 *
 * This is the whole reason P5 exported an Eden client instead of generating types: change
 * `appointmentView` in `apps/api/src/booking/read.ts` and the components below stop
 * compiling in the same commit. A hand-written mirror of this interface would keep
 * compiling and start being wrong.
 */
type Franchise = ReturnType<typeof api.v1.franchises>;

export type AppointmentsResponse = NonNullable<
  Awaited<ReturnType<Franchise["appointments"]["get"]>>["data"]
>;
export type AppointmentView = AppointmentsResponse["data"][number];

export type CalendarChrome = NonNullable<Awaited<ReturnType<Franchise["calendar"]["get"]>>["data"]>;
export type CalendarResourceView = CalendarChrome["resources"][number];

export type BlockersResponse = NonNullable<
  Awaited<ReturnType<Franchise["blockers"]["get"]>>["data"]
>;
export type BlockerView = BlockersResponse["data"][number];

export type RouteResponse = NonNullable<Awaited<ReturnType<Franchise["route"]["get"]>>["data"]>;
export type RouteStop = RouteResponse["stops"][number];

export interface MoveInput {
  readonly appointmentId: string;
  /** ISO 8601 with offset. Built by `lib/time.ts`, never by string surgery. */
  readonly start: string;
  readonly resourceId?: string;
  /** Set by a resize. Absent means the booking keeps its current length. */
  readonly durationMin?: number;
  readonly end?: string;
}

/**
 * The drag-and-drop write. docs/API.md: p95 under 300 ms, because the UI is optimistic and a
 * slow revert feels broken.
 *
 * The optimistic update itself lives at the call site rather than here: it needs to know
 * which window's cache entry the event is in, and this hook deliberately does not. What it
 * does own is throwing the 409 in a shape the conflict toast can read.
 */
export function useMoveAppointment(franchiseId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: MoveInput) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .appointments({ appointmentId: input.appointmentId })
          .patch({
            start: input.start,
            ...(input.resourceId === undefined ? {} : { resource_id: input.resourceId }),
            ...(input.durationMin === undefined ? {} : { duration_min: input.durationMin }),
            ...(input.end === undefined ? {} : { end: input.end }),
          }),
      ),
    onSettled: () => {
      // Both, and always. A move can free a slot that changes what a *blocker* overlaps, and
      // settling rather than succeeding means a rolled-back optimistic update is reconciled
      // against the server rather than left as the client's guess.
      void client.invalidateQueries({ queryKey: keys.appointmentsAll(franchiseId) });
      // A move frees one time and takes another, so every offered slot list is now a guess.
      void client.invalidateQueries({ queryKey: keys.slotsAll(franchiseId) });
    },
  });
}

export function useSetStatus(franchiseId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { appointmentId: string; status: string; reason?: string }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .appointments({ appointmentId: input.appointmentId })
          .status.post({
            status: input.status,
            ...(input.reason === undefined ? {} : { reason: input.reason }),
          }),
      ),
    onSettled: (_data, _error, input) => {
      void client.invalidateQueries({ queryKey: keys.appointmentsAll(franchiseId) });
      void client.invalidateQueries({ queryKey: keys.slotsAll(franchiseId) });
      // A status change schedules or cancels notifications (P6), so the job card's timeline
      // is stale the moment this returns.
      void client.invalidateQueries({
        queryKey: keys.notifications(franchiseId, input.appointmentId),
      });
    },
  });
}

export function useUpdateDetails(franchiseId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      appointmentId: string;
      notes?: string | null;
      priceMinor?: number | null;
    }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .appointments({ appointmentId: input.appointmentId })
          .details.patch({
            ...(input.notes === undefined ? {} : { notes: input.notes }),
            ...(input.priceMinor === undefined ? {} : { price_minor: input.priceMinor }),
          }),
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.appointmentsAll(franchiseId) });
    },
  });
}

export function useCancelAppointment(franchiseId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { appointmentId: string; reason?: string }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .appointments({ appointmentId: input.appointmentId })
          .delete({ ...(input.reason === undefined ? {} : { reason: input.reason }) }),
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.appointmentsAll(franchiseId) });
      void client.invalidateQueries({ queryKey: keys.slotsAll(franchiseId) });
    },
  });
}

export function useAppointmentNotifications(franchiseId: string, appointmentId: string | null) {
  return useQuery({
    queryKey: keys.notifications(franchiseId, appointmentId ?? ""),
    enabled: appointmentId !== null,
    queryFn: () =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .appointments({ appointmentId: appointmentId as string })
          .notifications.get(),
      ),
  });
}

export function useJobPhotos(franchiseId: string, appointmentId: string | null) {
  return useQuery({
    queryKey: keys.photos(franchiseId, appointmentId ?? ""),
    enabled: appointmentId !== null,
    queryFn: () =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .appointments({ appointmentId: appointmentId as string })
          .photos.get(),
      ),
    // The URLs are presigned for five minutes. Caching the response for longer would hand a
    // technician an expired link the second time they open the same card.
    staleTime: 2 * 60_000,
  });
}

/**
 * Mint a URL, PUT the bytes to R2, then confirm.
 *
 * The PUT is a bare `fetch` and deliberately not the Eden client: it goes to Cloudflare, not
 * to this API, and sending our session cookie to a third-party origin would be a mistake
 * that `credentials: "include"` makes silently easy.
 */
export function useUploadPhoto(franchiseId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      appointmentId: string;
      file: File;
      kind: "before" | "after" | "damage";
    }) => {
      const minted = await unwrap(
        api.v1
          .franchises({ franchiseId })
          .appointments({ appointmentId: input.appointmentId })
          .photos["upload-url"].post(),
      );

      const put = await fetch(minted.upload_url, {
        method: "PUT",
        body: input.file,
        // No credentials, and no headers of ours: the presigned URL carries the whole
        // authorization, and an extra signed header would invalidate the signature.
        credentials: "omit",
      });

      if (!put.ok) throw new Error(`R2 upload failed: ${put.status}`);

      return unwrap(
        api.v1
          .franchises({ franchiseId })
          .appointments({ appointmentId: input.appointmentId })
          .photos.post({ r2_key: minted.r2_key, kind: input.kind }),
      );
    },
    onSettled: (_data, _error, input) => {
      void client.invalidateQueries({ queryKey: keys.photos(franchiseId, input.appointmentId) });
    },
  });
}

export function useDeletePhoto(franchiseId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { appointmentId: string; photoId: string }) =>
      unwrap(
        api.v1
          .franchises({ franchiseId })
          .appointments({ appointmentId: input.appointmentId })
          .photos({ photoId: input.photoId })
          .delete(),
      ),
    onSettled: (_data, _error, input) => {
      void client.invalidateQueries({ queryKey: keys.photos(franchiseId, input.appointmentId) });
    },
  });
}

export function useRoute(franchiseId: string | null, resourceId: string | null, date: string) {
  return useQuery({
    queryKey: keys.route(franchiseId ?? "", resourceId ?? "", date),
    enabled: franchiseId !== null && resourceId !== null,
    queryFn: () =>
      unwrap(
        api.v1
          .franchises({ franchiseId: franchiseId as string })
          .route.get({ query: { resourceId: resourceId as string, date } }),
      ),
  });
}

/**
 * The times that are actually free, as staff are allowed to book them.
 *
 * `…/slots` and not the public `…/availability`: that one enforces min-notice, which hides
 * the next four hours — the window a walk-in at the counter is standing in.
 */
export function useStaffSlots(
  franchiseId: string | null,
  serviceIds: readonly string[],
  from: string,
  to: string,
  resourceId: string | null,
) {
  const ids = [...serviceIds].sort().join(",");

  return useQuery({
    queryKey: keys.slots(franchiseId ?? "", ids, from, resourceId ?? ""),
    enabled: franchiseId !== null && ids !== "",
    queryFn: () =>
      unwrap(
        api.v1.franchises({ franchiseId: franchiseId as string }).slots.get({
          query: {
            serviceIds: ids,
            from,
            to,
            ...(resourceId === null ? {} : { resourceId }),
          },
        }),
      ),
  });
}

export type SlotsResponse = NonNullable<Awaited<ReturnType<Franchise["slots"]["get"]>>["data"]>;
export type SlotDayView = SlotsResponse["days"][number];
export type SlotView = SlotDayView["slots"][number];

export function useCreateBlocker(franchiseId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      kind: string;
      date: string;
      resourceId?: string;
      startTime?: string;
      endTime?: string;
      reason?: string;
      rrule?: string;
      repeatUntil?: string;
    }) =>
      unwrap(
        api.v1.franchises({ franchiseId }).blockers.post({
          kind: input.kind,
          date: input.date,
          ...(input.resourceId === undefined ? {} : { resource_id: input.resourceId }),
          ...(input.startTime === undefined ? {} : { start_time: input.startTime }),
          ...(input.endTime === undefined ? {} : { end_time: input.endTime }),
          ...(input.reason === undefined ? {} : { reason: input.reason }),
          ...(input.rrule === undefined ? {} : { rrule: input.rrule }),
          ...(input.repeatUntil === undefined ? {} : { repeat_until: input.repeatUntil }),
        }),
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.blockersAll(franchiseId) });
      void client.invalidateQueries({ queryKey: keys.slotsAll(franchiseId) });
    },
  });
}

export function useDeleteBlocker(franchiseId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (blockerId: string) =>
      unwrap(api.v1.franchises({ franchiseId }).blockers({ blockerId }).delete()),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.blockersAll(franchiseId) });
      void client.invalidateQueries({ queryKey: keys.slotsAll(franchiseId) });
    },
  });
}

/**
 * The staff-side booking write. `POST …/appointments`, which lands `confirmed` whatever the
 * franchise's `auto_confirm` says — see the note in `apps/api/src/routes/appointments.ts`:
 * staff creating a booking *is* the confirmation, because a human is the one making it.
 *
 * `source` distinguishes the counter from the phone. Both are staff-entered and neither is a
 * web booking, but "how did this job reach us" is a question the HQ demand panel asks, and
 * answering `phone` for someone standing in the workshop would be answering it wrong.
 */
export type BookingSource = "phone" | "walkin";

export function useCreateAppointment(franchiseId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      serviceIds: readonly string[];
      start: string;
      end: string;
      resourceId?: string;
      customer: { name?: string; email?: string; phone?: string };
      notes?: string;
      source: BookingSource;
    }) =>
      unwrap(
        api.v1.franchises({ franchiseId }).appointments.post({
          service_ids: [...input.serviceIds],
          start: input.start,
          end: input.end,
          ...(input.resourceId === undefined ? {} : { resource_id: input.resourceId }),
          customer: input.customer,
          ...(input.notes === undefined ? {} : { notes: input.notes }),
          source: input.source,
        }),
      ),
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.appointmentsAll(franchiseId) });
      void client.invalidateQueries({ queryKey: keys.slotsAll(franchiseId) });
    },
  });
}

export function useServices(franchiseId: string | null) {
  return useQuery({
    queryKey: ["services", franchiseId ?? ""] as const,
    enabled: franchiseId !== null,
    queryFn: () => unwrap(api.v1.franchises({ franchiseId: franchiseId as string }).services.get()),
    staleTime: 5 * 60_000,
  });
}
